/**
 * VideoConferenceHandler
 *
 * Handles incoming video conference notifications from Pusher.
 * Creates banner notifications and manages video call lifecycle.
 */

import type {
  VideoConferenceCallInfo,
  VideoConferenceNotificationData,
} from './index.types';

import { VIDEO_CALL_TIMEOUT } from 'Constants/env';
import {
  STORE_CONVERSATION,
  STORE_NOTIFICATION,
  STORE_PERSON,
  STORE_PREFERENCE,
  STORE_UI,
} from 'Constants/stores';
import type { IMessageModelConference } from 'Models/MessageModel';
import { useNotificationStore } from 'Modules/notification/store';
import {
  checkWebNotificationEligibility,
  handleDesktopNotificationClick,
} from 'Modules/notification/utils';
import { getDesktopNotificationContent } from 'Modules/notification/utils/desktopNotificationContent';
import { AudioService } from 'Modules/softphone/services/AudioService';
import { CallManager } from 'Modules/softphone/services/CallManager';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { createLogger } from 'Modules/softphone/utils/logger';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';

const logger = createLogger('VideoConferenceHandler');

export class VideoConferenceHandler {
  private activeVideoCalls: Map<string, VideoConferenceCallInfo> = new Map();
  private videoTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private rootStoreProps?: RootStoreProps;

  /**
   * Set RootStoreProps for store access (PersonStore, UiStore)
   */
  setRootStoreProps(rootStoreProps: RootStoreProps): void {
    this.rootStoreProps = rootStoreProps;
    logger.debug('RootStoreProps set');
  }

  /**
   * Handle incoming video conference notification from Pusher
   */
  async handleIncomingVideoConference(
    conference: IMessageModelConference,
    personId: number,
    conversationId?: string
  ): Promise<void> {
    const conferenceId = conference.id;

    logger.info('Handling incoming video conference', {
      conferenceId,
      personId,
    });

    // Check if already in list (avoid duplicates)
    if (this.activeVideoCalls.has(conferenceId)) {
      logger.warn('Video conference already in list, ignoring', {
        conferenceId,
      });
      return;
    }

    // Get caller info (person details)
    const callerInfo = await this.getCallerInfo(personId);

    // Ad-hoc video invites don't carry attendee info, so group vs. 1:1 is
    // derived from the conversation's grouping instead.
    const isGroupConference = await this.resolveIsGroupConference(
      conversationId
    );

    // Play audio notification (if enabled in preferences)
    await this.playVideoStartAudio();

    // Create notification
    const notificationId = this.createVideoConferenceNotification({
      conferenceId,
      personId,
      displayName: callerInfo.displayName,
      avatarUrl: callerInfo.avatarUrl,
    });

    // Trigger desktop notification if window not in foreground
    const desktopNotification = !document.hasFocus()
      ? this.triggerDesktopNotification(
          conference,
          callerInfo.displayName,
          isGroupConference
        )
      : undefined;

    // Store call info
    this.activeVideoCalls.set(conferenceId, {
      conferenceId,
      personId,
      conference,
      notificationId,
      desktopNotification,
    });

    // Wire up notification callbacks
    this.wireNotificationCallbacks(conferenceId, notificationId);

    // Start timeout (60 seconds)
    this.startVideoTimeout(conferenceId);

    logger.info('Video conference notification created', {
      conferenceId,
      notificationId,
    });
  }

  /**
   * Play video start notification audio
   */
  private async playVideoStartAudio(): Promise<void> {
    await this.playVideoAudio('videoStart', 'Video start audio played');
  }

  /**
   * Play video participant joined notification audio
   */
  async playVideoJoinAudio(): Promise<void> {
    await this.playVideoAudio('videoJoin', 'Video join audio played');
  }

  /**
   * Play video participant left notification audio
   */
  async playVideoLeaveAudio(): Promise<void> {
    await this.playVideoAudio('videoLeave', 'Video leave audio played');
  }

  /**
   * Play video conference ended notification audio
   */
  async playVideoEndedAudio(): Promise<void> {
    await this.playVideoAudio('videoEnded', 'Video ended audio played');
  }

  /**
   * Generic method to play video notification audio
   * Checks if notification audio is enabled in preferences
   */
  private async playVideoAudio(
    audioType: 'videoStart' | 'videoJoin' | 'videoLeave' | 'videoEnded',
    successMessage: string
  ): Promise<void> {
    logger.info(`[VideoConference] Attempting to play ${audioType} audio`, {
      hasRootStoreProps: !!this.rootStoreProps,
    });

    if (!this.rootStoreProps) {
      logger.warn(
        `[VideoConference] Cannot play ${audioType}: rootStoreProps not set`
      );
      return;
    }

    const preferenceStore = this.rootStoreProps[STORE_PREFERENCE];
    if (!preferenceStore) {
      logger.warn(
        `[VideoConference] Cannot play ${audioType}: preferenceStore not available`
      );
      return;
    }

    // Check if notification audio is enabled
    const audioEnabled = preferenceStore.preferences?.notificationAudio;
    logger.info(`[VideoConference] Notification audio preference`, {
      audioEnabled,
      hasPreferences: !!preferenceStore.preferences,
    });

    if (!audioEnabled) {
      logger.debug(
        `[VideoConference] Notification audio disabled in preferences, skipping ${audioType}`
      );
      return;
    }

    try {
      const audioService = AudioService.getInstance();
      logger.info(
        `[VideoConference] Playing ${audioType} audio via AudioService`
      );
      await audioService.play(audioType);
      logger.info(`[VideoConference] ${successMessage}`);
    } catch (error) {
      logger.error(`[VideoConference] Failed to play ${audioType} audio`, {
        error: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
      });
    }
  }

  /**
   * Get caller information from PersonStore
   */
  private async getCallerInfo(
    personId: number
  ): Promise<{ displayName?: string; avatarUrl?: string }> {
    if (!this.rootStoreProps) {
      logger.warn('RootStoreProps not available, skipping caller lookup');
      return {};
    }

    const personStore = this.rootStoreProps[STORE_PERSON];
    if (!personStore) {
      logger.warn('PersonStore not available');
      return {};
    }

    try {
      // Load person if not in cache
      await personStore.loadPersonByIdGetIfMissingGet(personId.toString());

      // Get person from cache
      const person = personStore.selectPersonValueById(personId);

      if (person) {
        return {
          displayName: person.name || undefined,
          avatarUrl: person.avatarUrl || undefined,
        };
      }
    } catch (error) {
      logger.warn('Failed to get person info', {
        personId,
        error: error instanceof Error ? error.message : String(error),
      });
    }

    return {};
  }

  /**
   * Whether the conference's conversation is a group (not one-to-one).
   * Conference invites don't carry attendee info, so this is resolved from
   * the conversation's grouping instead.
   */
  private async resolveIsGroupConference(
    conversationId?: string
  ): Promise<boolean> {
    if (!conversationId) {
      return false;
    }

    const conversationStore = this.rootStoreProps?.[STORE_CONVERSATION];
    if (!conversationStore) {
      logger.warn('ConversationStore not available, assuming 1:1 conference');
      return false;
    }

    try {
      const convPromise = conversationStore.loadConversationByIdIfMissingGet(
        conversationId,
        false,
        true
      );

      const conv = await convPromise;
      return conv?.data?.grouping !== 'OneOnOne';
    } catch (error) {
      logger.warn('Failed to resolve conversation grouping', {
        conversationId,
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  /**
   * Create video conference notification banner
   */
  private createVideoConferenceNotification(
    data: VideoConferenceNotificationData
  ): string | number {
    const notificationStore = useNotificationStore.getState();

    logger.info('Creating video conference notification', {
      conferenceId: data.conferenceId,
      displayName: data.displayName,
      avatarUrl: data.avatarUrl,
      isVideo: true,
    });

    const notificationId = notificationStore.addNotify({
      priority: 'call',
      position: 'tr',
      dismissible: 'none',
      autoDismiss: false,
      inboundCallData: {
        callId: data.conferenceId,
        phoneNumber: '', // No phone number for video conferences
        displayName: data.displayName,
        avatarUrl: data.avatarUrl,
        isVideo: true,
        onAccept: data.onJoin,
        onDecline: data.onDecline,
      },
    });

    return notificationId;
  }

  /**
   * Trigger desktop (OS-native) notification for an incoming video conference invite.
   *
   * The legacy softphone skips its own video-invite desktop notification whenever
   * the new softphone is active, assuming this one handles it - so this is the
   * only place a video conference invite triggers a desktop notification.
   */
  private triggerDesktopNotification(
    conference: IMessageModelConference,
    displayName?: string,
    isGroupConference?: boolean
  ): Notification | undefined {
    const notificationStore = this.rootStoreProps?.[STORE_NOTIFICATION];
    const webNotificationsEnabled =
      notificationStore?.webNotificationsEnabled ?? false;

    const { canShow, reason } = checkWebNotificationEligibility(
      webNotificationsEnabled
    );

    if (!canShow) {
      logger.debug('Cannot show desktop notification', { reason });
      return undefined;
    }

    const { title, body } = getDesktopNotificationContent({
      isVideo: true,
      displayName,
      isGroupConference,
      groupName: conference.displayName,
    });

    try {
      const tag = `video-conference-${conference.id}`;
      const notification = new Notification(title, {
        body,
        icon: '/icons/video-call.png',
        tag,
        requireInteraction: true,
        silent: true, // Application manages audio playback via AudioService
      });

        // Restore/focus via main process — window.focus() cannot unminimize Electron windows.
      notification.onclick = () => {
        handleDesktopNotificationClick(notification, tag);
      };

      logger.debug('Desktop notification created', {
        conferenceId: conference.id,
        title,
        body,
      });

      return notification;
    } catch (error) {
      logger.error('Failed to create desktop notification', {
        error: (error as Error).message,
        conferenceId: conference.id,
      });
      return undefined;
    }
  }

  /**
   * Wire up notification callbacks (Join/Decline)
   */
  private wireNotificationCallbacks(
    conferenceId: string,
    notificationId: string | number
  ): void {
    const notificationStore = useNotificationStore.getState();
    const position = 'tr';
    const notifications = notificationStore.notifications.get(position) || [];
    const notification = notifications.find((n) => n.id === notificationId);

    if (notification?.inboundCallData) {
      notification.inboundCallData.onAccept = () =>
        this.handleJoin(conferenceId);
      notification.inboundCallData.onDecline = () =>
        this.handleDecline(conferenceId);

      // Convert notificationId to string for updateNotification
      const idAsString =
        typeof notificationId === 'string'
          ? notificationId
          : notificationId.toString();

      notificationStore.updateNotification(position, idAsString, notification);
    }
  }

  /**
   * Start 60-second timeout for video call
   */
  private startVideoTimeout(conferenceId: string): void {
    const timeout = setTimeout(() => {
      logger.info('Video conference timeout expired', { conferenceId });
      this.removeVideoConference(conferenceId);
    }, VIDEO_CALL_TIMEOUT);

    this.videoTimeouts.set(conferenceId, timeout);
  }

  /**
   * Clear timeout for video call
   */
  private clearVideoTimeout(conferenceId: string): void {
    const timeout = this.videoTimeouts.get(conferenceId);
    if (timeout) {
      clearTimeout(timeout);
      this.videoTimeouts.delete(conferenceId);
    }
  }

  /**
   * Handle Join button click
   */
  private handleJoin(conferenceId: string): void {
    logger.info('User joined video conference', { conferenceId });

    const callInfo = this.activeVideoCalls.get(conferenceId);
    if (!callInfo) {
      logger.warn('Video conference not found', { conferenceId });
      return;
    }

    // Send analytics event
    pushToGTMDataLayer('video_call_accepted', { conference_id: conferenceId });

    // Navigate to video conference
    if (this.rootStoreProps) {
      const uiStore = this.rootStoreProps[STORE_UI];
      if (uiStore) {
        uiStore.navigateVideoConferenceToSession(conferenceId, null);
      }
    }

    // Hang up the active softphone call — can't be in both a call and a video conference
    const store = useSoftphoneStore.getState();
    const currentCallId = store.currentCallId;
    if (currentCallId) {
      const callManager = CallManager.getInstance();
      callManager.hangUp(currentCallId).catch((error: Error) => {
        logger.warn('Failed to hang up call after joining video conference', {
          error: error.message,
          callId: currentCallId,
        });
      });
    }

    // Remove notification and cleanup
    this.removeVideoConference(conferenceId);
  }

  /**
   * Handle Decline button click
   */
  private handleDecline(conferenceId: string): void {
    logger.info('User declined video conference', { conferenceId });

    // Send analytics event
    pushToGTMDataLayer('video_call_declined', { conference_id: conferenceId });

    this.removeVideoConference(conferenceId);
  }

  /**
   * Remove video conference (dismiss notification and cleanup)
   */
  private removeVideoConference(conferenceId: string): void {
    const callInfo = this.activeVideoCalls.get(conferenceId);
    if (!callInfo) {
      return;
    }

    // Clear timeout
    this.clearVideoTimeout(conferenceId);

    // Remove notification
    if (callInfo.notificationId) {
      const notificationStore = useNotificationStore.getState();
      notificationStore.removeNotify('tr', callInfo.notificationId);
    }

    // Close desktop notification, if one was shown
    callInfo.desktopNotification?.close();

    // Remove from active calls
    this.activeVideoCalls.delete(conferenceId);

    logger.debug('Video conference removed', { conferenceId });
  }

  /**
   * Cleanup all video conferences
   */
  cleanup(): void {
    // Clear all timeouts
    this.videoTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.videoTimeouts.clear();

    // Remove all notifications
    const notificationStore = useNotificationStore.getState();
    this.activeVideoCalls.forEach((callInfo) => {
      if (callInfo.notificationId) {
        notificationStore.removeNotify('tr', callInfo.notificationId);
      }
      callInfo.desktopNotification?.close();
    });

    this.activeVideoCalls.clear();
    logger.info('Video conference handler cleaned up');
  }
}
